// ============================================================================
// The path of one order, on this page (search and every drill-down row open it).
//
// Read top down (user, 2026-10-12): a line of order facts, the three facts that matter (promised,
// shipments, escalations), then a verdict card in plain words — the headline, where the order is
// stuck and why, what that holds up, who is on it, and earlier lapses that no longer matter. Below it
// one route per group: a single PO is drawn as step cards (click one for its details), several POs
// as a grid. Only the stage a PO is stuck at shows its delay; the stages after it are blank until
// Shipment, which is judged on its own SLA. Service POs (site audit, installation…) are not shown:
// they deliver no goods. "Open in OMS" leaves for the full order page.
// ============================================================================
import { ExportOutlined, FlagFilled, WarningFilled } from "@ant-design/icons";
import { Alert, Button, Collapse, Drawer, Popover, Skeleton, Switch, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { omsOrderPath } from "../../../../oms/format";
import { getOrderPath } from "../../api/orders";
import { fmtWeight } from "../../format";
import { OcOrderPath, OcOrderPathCell, OcOrderPathRow, OcPathEscalation, OcSlaLevel } from "../../types/orders";
import styles from "../../index.module.scss";

export interface OrderPathRequest {
  salesOrderId: number;
  allocationIds?: number[]; // the PO(s) it was opened from: highlighted, always shown, listed first
  title?: string;
}

// Dates, not weekdays (user, 2026-10-07): "6 Oct 19:56".
const fmtAt = (v: string | null | undefined) => (v ? dayjs(v).format("D MMM HH:mm") : "–");
const fmtDay = (v: string | null | undefined) => (v ? dayjs(v).format("D MMM YYYY") : "–");
const human = (s: string | null | undefined) => (s ? s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "–");
// An escalation team as OMS names it (backend `team_label`): short keys stay upper ("cs" → "CS").
const teamLabel = (c: string | null | undefined) =>
  !c ? "No team" : c.length <= 3 ? c.toUpperCase() : c.replace(/_/g, " ").replace(/\b\w/g, (x) => x.toUpperCase());

// How far past the breach line, in words: "20 min" / "44h 40m" / "3d 4h".
const lateText = (m: number | null | undefined) =>
  m == null
    ? ""
    : m < 60
      ? `${m} min`
      : m < 2880
        ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`
        : `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`;

// The stages in path order; Shipment / Re-attempt are the end stages (judged on their own SLA).
const ORDER = ["order_placed", "po_raise", "first_mile", "grn", "transfer", "picklist", "shipment", "reattempt"];
const END = new Set(["shipment", "reattempt"]);

// A PO that needs a look: breached / at risk / tech error / open escalation, or still open.
const isProblem = (r: OcOrderPathRow) =>
  r.kind !== "cancelled" &&
  Object.values(r.cells).some(
    (c) =>
      ["breached", "major", "minor", "excused"].includes(c.sla?.level ?? "") ||
      (c.tech ?? []).length > 0 ||
      c.escalations.some((e) => !e.resolved_at) ||
      c.state === "waiting",
  );

const LEVEL: Record<OcSlaLevel, string> = {
  on_track: "On time",
  minor: "At risk · minor",
  major: "At risk · major",
  breached: "Breached",
  excused: "Excused by escalation",
  exempt: "Not judged",
};

// One look for a step, shared by the step cards and the grid: done, stuck / late, at risk, in
// progress, excused, or blank (not started, or held up by an earlier stuck stage).
type Tone = "done" | "late" | "risk" | "ok" | "excused" | "blank";
const TONE_CLS: Record<Tone, string> = {
  done: styles.tDone,
  late: styles.tLate,
  risk: styles.tRisk,
  ok: styles.tOk,
  excused: styles.tExcused,
  blank: styles.tBlank,
};

interface CellView {
  tone: Tone;
  status: string;
  line: string;
  note: string;
}

const cellView = (key: string, cell: OcOrderPathCell, current: boolean): CellView => {
  const lvl = cell.sla?.level;
  const late = lvl === "breached" ? (cell.sla?.late_min != null ? `${lateText(cell.sla.late_min)} late` : "Late") : "";
  if (cell.blocked_by) return { tone: "blank", status: "Not started", line: "", note: "" };
  if (cell.state === "done") {
    return { tone: "done", status: "Done", line: fmtAt(cell.actual_at), note: late || (lvl === "excused" ? "Late, excused" : "") };
  }
  const line = cell.live ?? (current ? "Waiting" : "");
  if (lvl === "breached") return { tone: "late", status: current && !END.has(key) ? "Stuck" : "Late", line, note: late };
  if (lvl === "major" || lvl === "minor") return { tone: "risk", status: "At risk", line, note: cell.sla?.note ?? "" };
  if (lvl === "excused") return { tone: "excused", status: "Excused", line, note: cell.sla?.excused_by ? `By ${cell.sla.excused_by}` : "" };
  return { tone: current ? "ok" : "blank", status: current ? "In progress" : "Not started", line, note: "" };
};

const STATUS: Record<NonNullable<OcOrderPath["status"]>, { label: string; tone: Tone }> = {
  at_risk: { label: "At risk", tone: "risk" },
  breached: { label: "Late", tone: "late" },
  excused: { label: "Excused", tone: "excused" },
  on_track: { label: "On track", tone: "ok" },
  delivered: { label: "Delivered", tone: "done" },
  delivered_late: { label: "Delivered", tone: "risk" },
  payment_pending: { label: "Payment pending", tone: "risk" },
  none: { label: "No POs", tone: "blank" },
};

// What the owning team has to do to move a stage on, in a few words.
const ACTION: Record<string, string> = {
  po_raise: "raise the PO",
  first_mile: "get the goods to the warehouse",
  grn: "mark the GRN",
  transfer: "dispatch the transfer",
  picklist: "complete the picklist",
};
const actionFor = (key: string, live: string | null | undefined) => {
  const s = (live ?? "").toLowerCase();
  if (key === "shipment" || key === "reattempt") {
    if (s.includes("transit")) return "track the delivery";
    if (s.includes("attempted")) return "re-attempt the delivery";
    if (s.includes("booked") && !s.includes("not booked")) return "get it picked up";
    return key === "reattempt" ? "book the re-attempt" : "book a truck";
  }
  return ACTION[key] ?? "";
};

// The delivery stage a PO is judged on: Re-attempt once re-attempted, else Shipment (as the backend).
const deliveryCell = (r: OcOrderPathRow) => (r.cells.reattempt?.sla ? r.cells.reattempt : r.cells.shipment);

type OrderEscalation = OcOrderPath["escalations"][number];
// The same escalation raised on several POs (one per PO's leg) reads as one: "… on 3 POs".
const groupEscalations = (list: OrderEscalation[]) => {
  const byKey = new Map<string, { e: OrderEscalation; pos: Set<string> }>();
  list.forEach((e) => {
    const k = [e.issue_type, e.category, e.remarks, (e.raised_at ?? "").slice(0, 16)].join("|");
    const g = byKey.get(k);
    if (g) g.pos.add(e.po_number);
    else byKey.set(k, { e, pos: new Set([e.po_number]) });
  });
  return Array.from(byKey.values());
};

const dayWord = (d: dayjs.Dayjs) =>
  d.isSame(dayjs(), "day")
    ? `Today, ${d.format("D MMM")}`
    : d.isSame(dayjs().add(1, "day"), "day")
      ? `Tomorrow, ${d.format("D MMM")}`
      : d.isSame(dayjs().subtract(1, "day"), "day")
        ? `Yesterday, ${d.format("D MMM")}`
        : d.format("D MMM YYYY");

const TAG_COLOR = (t: string) => (t.startsWith("Excluded") ? "default" : t === "Re-raised" ? "purple" : "orange");

const OrderPathDrawer: React.FC<{ request: OrderPathRequest | null; onClose: () => void }> = ({ request, onClose }) => {
  const [data, setData] = useState<OcOrderPath | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    if (!request) return;
    let alive = true;
    getOrderPath(request.salesOrderId, request.allocationIds)
      .then((res) => alive && setData(res))
      .catch((e) => alive && setError(e?.response?.data?.detail ?? e?.message ?? "Could not load the order path"));
    return () => {
      alive = false;
    };
  }, [request]);

  return (
    <Drawer
      open={!!request}
      onClose={onClose}
      width="min(1400px, 98vw)"
      destroyOnClose
      title={data ? `${data.order_id} · ${data.facts.client || "–"}` : request?.title ?? "Path"}
      extra={
        request ? (
          <Button
            size="small"
            icon={<ExportOutlined />}
            href={omsOrderPath(request.salesOrderId, { allocationId: request.allocationIds?.[0] })}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in OMS
          </Button>
        ) : null
      }
    >
      {error ? <Alert type="error" showIcon message={error} /> : null}
      {!data && !error ? <Skeleton active paragraph={{ rows: 6 }} /> : null}
      {data ? <PathView data={data} /> : null}
    </Drawer>
  );
};

const PathView: React.FC<{ data: OcOrderPath }> = ({ data }) => {
  const f = data.facts;
  const labelOf = useMemo(() => {
    const m = new Map(data.columns.map((c) => [c.key, c.label]));
    return (k: string) => m.get(k) ?? human(k);
  }, [data.columns]);
  // Goods POs only: a service PO (site audit, installation…) moves no goods (user, 2026-10-12).
  const goods = data.rows.filter((r) => r.kind !== "service");
  const live = goods.filter((r) => r.kind !== "cancelled");
  const openEsc = data.escalations.filter((e) => !e.resolved_at);
  const opened = goods.filter((r) => r.highlight);
  // Escalations on the PO(s) it was opened from: marked, listed first, and the list opens for them.
  const openedPos = new Set(opened.map((r) => r.po_number));
  const openedEsc = data.escalations.filter((e) => openedPos.has(e.po_number));
  const openedEscOpen = openedEsc.filter((e) => !e.resolved_at).length;

  // Promised: day and time from ONE delivery — the earliest still open, of the opened PO(s) when it was
  // opened from one (user, 2026-10-12: no latest date beside another PO's earliest time).
  const focus = live.filter((r) => r.highlight);
  const scoped = focus.length ? focus : live;
  const nextLine = scoped
    .map(deliveryCell)
    .filter((c) => c && c.state === "waiting" && c.planned_at)
    .map((c) => c!.planned_at as string)
    .sort()[0];
  const targets = scoped.map((r) => r.target_delivery_date).filter(Boolean).sort() as string[];
  const promised = nextLine
    ? `${dayWord(dayjs(nextLine))} · by ${dayjs(nextLine).format("HH:mm")}`
    : targets.length
      ? dayWord(dayjs(targets[targets.length - 1]))
      : f.promised_delivery
        ? fmtDay(f.promised_delivery)
        : "–";
  const teams = Array.from(new Set(openEsc.map((e) => teamLabel(e.category))));

  return (
    <div className={styles.path}>
      <div className={styles.pathFacts}>
        <span>
          {f.delivery_type ? human(f.delivery_type) : "–"} · {f.destination ?? "–"}
          {f.origin ? ` (from ${f.origin})` : ""}
        </span>
        <span>Raised {fmtAt(f.raised_at)}</span>
        <span>
          {live.length} PO{live.length === 1 ? "" : "s"} to deliver
        </span>
        {f.weight_kg ? <span>{fmtWeight(f.weight_kg)}</span> : null}
        {f.payment_pending ? <Tag color="gold">Payment pending</Tag> : null}
        {f.oms_status_lag ? (
          <Tooltip title="Every PO is delivered, but OMS still says partly delivered: a PO that rode another PO's truck is not counted by OMS's status update.">
            <Tag color="blue">All POs delivered · OMS status not updated</Tag>
          </Tooltip>
        ) : null}
      </div>

      <div className={styles.keyFacts}>
        <div>
          <div className={styles.keyFactLabel}>Promised</div>
          <div className={styles.keyFactValue}>{promised}</div>
        </div>
        <div>
          <div className={styles.keyFactLabel}>Shipments</div>
          <div className={styles.keyFactValue}>{f.shipments ? `${f.shipments} created` : "None created yet"}</div>
        </div>
        <div>
          <div className={styles.keyFactLabel}>Escalations</div>
          <div className={styles.keyFactValue}>
            {openEsc.length
              ? `${groupEscalations(openEsc).length} open${teams.length ? ` · ${teams.join(", ")}` : ""}`
              : data.escalations.length
                ? `${groupEscalations(data.escalations).length}, all resolved`
                : "None"}
          </div>
        </div>
      </div>

      <VerdictCard data={data} rows={live} labelOf={labelOf} />

      {opened.length ? (
        <div className={styles.openedBar}>
          You opened {opened.length === 1 ? "PO" : `${opened.length} POs`}{" "}
          {opened.map((r) => (
            <span key={r.allocation_id} className={styles.mono}>
              {r.po_number}{" "}
            </span>
          ))}
          <span className={styles.muted}>· highlighted below; the other POs of this order are shown for context.</span>
        </div>
      ) : null}

      <PoGroups data={data} rows={goods} labelOf={labelOf} />

      <div className={styles.pathLegend}>
        <span className={`${styles.legendKey} ${styles.tDone}`}>Done</span>
        <span className={`${styles.legendKey} ${styles.tLate}`}>Stuck / late</span>
        <span className={`${styles.legendKey} ${styles.tRisk}`}>At risk</span>
        <span className={`${styles.legendKey} ${styles.tExcused}`}>Excused</span>
        <span className={`${styles.legendKey} ${styles.tBlank}`}>Not started</span>
        <span>
          <FlagFilled className={styles.flagOpen} /> open escalation · <FlagFilled className={styles.flagClosed} /> resolved
        </span>
        <span>
          <WarningFilled className={styles.flagOpen} /> tech error
        </span>
        <span>Only the stuck stage and Shipment show a delay; the stages between are blank.</span>
      </div>

      <Collapse
        size="small"
        ghost
        defaultActiveKey={openEsc.length || openedEsc.length ? ["esc"] : []}
        items={[
          {
            key: "esc",
            label: (
              <span>
                {`Escalations on this order (${data.escalations.length}${openEsc.length ? `, ${openEsc.length} open` : ""})`}
                {openedEsc.length ? (
                  <span className={styles.openedEscCount}>
                    {` · ${openedEsc.length} on the PO you opened${openedEscOpen ? `, ${openedEscOpen} open` : ""}`}
                  </span>
                ) : null}
              </span>
            ),
            children: <EscalationTable rows={data.escalations} openedPos={openedPos} />,
          },
        ]}
      />
    </div>
  );
};

// The verdict in plain words (user, 2026-10-12): what needs doing now, and by whom.
//  - Opened from a PO: that PO leads — where it is stuck and what to do, what that holds up, who is on
//    it, its own earlier lapses — then one line sums up the rest of the order.
//  - Opened from an order: one line per team to chase, worst first; POs on track share one line.
// Delivered POs never set the headline (the backend builds it from open deliveries).
interface StageLine {
  key: string;
  label: string;
  tone: "late" | "risk" | "ok";
  lateMin: number;
  order: number;
  count: number;
  body: React.ReactNode;
}

const RANK = { late: 0, risk: 1, ok: 2 };

const stageLines = (rows: OcOrderPathRow[], data: OcOrderPath, labelOf: (k: string) => string, byTeam: boolean): StageLine[] => {
  const owner = new Map(data.columns.map((c) => [c.key, c.owner]));
  const escTeams = new Map<string, Set<string>>(); // PO → teams of its open escalations
  data.escalations
    .filter((e) => !e.resolved_at)
    .forEach((e) => escTeams.set(e.po_number, new Set([...Array.from(escTeams.get(e.po_number) ?? []), teamLabel(e.category)])));
  const byStage = new Map<string, OcOrderPathRow[]>();
  rows.forEach((r) => {
    if (r.waiting_on) byStage.set(r.waiting_on, [...(byStage.get(r.waiting_on) ?? []), r]);
  });
  return Array.from(byStage.entries())
    .map(([key, rs]) => {
      const cells = rs.map((r) => r.cells[key]);
      const lateMin = Math.max(-1, ...cells.map((c) => (c.sla?.level === "breached" ? c.sla?.late_min ?? 0 : -1)));
      const late = cells.filter((c) => c.sla?.level === "breached").length;
      const atRisk = cells.filter((c) => ["major", "minor"].includes(c.sla?.level ?? "")).length;
      const tone: StageLine["tone"] = lateMin >= 0 ? "late" : atRisk ? "risk" : "ok";
      const one = rs.length === 1 ? rs[0] : null;
      const team = owner.get(key) ?? "";
      const action = tone === "ok" ? "" : actionFor(key, (one ? one.cells[key] : cells.find((c) => c.live))?.live);
      const teams = Array.from(new Set(rs.flatMap((r) => Array.from(escTeams.get(r.po_number) ?? []))));
      const body = (
        <span>
          <span className={styles.strong}>{labelOf(key)}</span>
          {one ? (
            <>
              {" · "}
              <span className={styles.mono}>{one.po_number}</span>
              {one.vendor ? ` (${one.vendor})` : ""}
              {one.cells[key].live ? ` · ${one.cells[key].live}` : ""}
            </>
          ) : (
            ` · ${rs.length} POs`
          )}
          {lateMin >= 0 ? (
            <span className={styles.lateText}>
              {" · "}
              {rs.length > 1 ? `${late} late, worst ` : ""}
              {lateMin > 0 ? `${lateText(lateMin)} late` : "late"}
            </span>
          ) : atRisk ? (
            <span className={styles.riskText}> · {rs.length > 1 ? `${atRisk} at risk` : "at risk"}</span>
          ) : null}
          {action ? ` → ${byTeam ? "" : `${team} to `}${action}` : null}
          {teams.length ? <span className={styles.muted}> · escalated ({teams.join(", ")})</span> : null}
        </span>
      );
      const label = byTeam ? team || labelOf(key) : tone === "late" ? "Stuck at" : tone === "risk" ? "At risk" : "Now at";
      return { key, label, tone, lateMin, order: ORDER.indexOf(key), count: rs.length, body };
    })
    .sort((a, b) => RANK[a.tone] - RANK[b.tone] || b.lateMin - a.lateMin || a.order - b.order);
};

// The rest of the order, in one line: "3 other POs · 2 late · 1 delivered".
const restSummary = (rows: OcOrderPathRow[]) => {
  let late = 0, risk = 0, ok = 0, delivered = 0, deliveredLate = 0;
  rows.forEach((r) => {
    const c = deliveryCell(r);
    const lvl = c?.sla?.level;
    if (!r.waiting_on || c?.state === "done") {
      delivered += 1;
      if (lvl === "breached") deliveredLate += 1;
    } else if (Object.values(r.cells).some((x) => x.state === "waiting" && x.sla?.level === "breached")) late += 1;
    else if (Object.values(r.cells).some((x) => x.state === "waiting" && ["major", "minor"].includes(x.sla?.level ?? ""))) risk += 1;
    else ok += 1;
  });
  const parts = [
    late ? `${late} late` : "",
    risk ? `${risk} at risk` : "",
    ok ? `${ok} on track` : "",
    delivered ? `${delivered} delivered${deliveredLate ? ` (${deliveredLate} late)` : ""}` : "",
  ].filter(Boolean);
  return `${rows.length} other PO${rows.length === 1 ? "" : "s"} · ${parts.join(" · ")}`;
};

const VerdictCard: React.FC<{ data: OcOrderPath; rows: OcOrderPathRow[]; labelOf: (k: string) => string }> = ({ data, rows, labelOf }) => {
  const status = data.status ? STATUS[data.status] : null;
  const toneCls = status ? TONE_CLS[status.tone] : TONE_CLS.blank;
  const focus = rows.filter((r) => r.highlight);
  const focusMode = focus.length > 0 && focus.length < rows.length;
  const scope = focusMode ? focus : rows;
  const scopePos = new Set(scope.map((r) => r.po_number));

  // Where it waits: per stage (opened PO) or per team (order); on-track POs of an order share a line.
  let lines = stageLines(scope, data, labelOf, !focusMode);
  if (!focusMode) {
    const ok = lines.filter((l) => l.tone === "ok");
    if (ok.length > 1) {
      const n = ok.reduce((s, l) => s + l.count, 0);
      lines = [
        ...lines.filter((l) => l.tone !== "ok"),
        {
          key: "on_track", label: "On track", tone: "ok", lateMin: -1, order: 99, count: n,
          body: <span>{`${n} POs · at ${ok.sort((a, b) => a.order - b.order).map((l) => labelOf(l.key)).join(", ")}`}</span>,
        },
      ];
    }
  }
  const shown = lines.slice(0, 4);

  // What the opened PO's stuck stage holds up: the blank stages after it, then the delivery.
  let blocks = "";
  const firstUp = focusMode || scope.length === 1 ? lines.slice().sort((a, b) => a.order - b.order)[0] : undefined;
  if (firstUp && !END.has(firstUp.key)) {
    const held = new Set<string>();
    scope
      .filter((r) => r.waiting_on === firstUp.key)
      .forEach((r) =>
        ORDER.forEach((k) => {
          const c = r.cells[k];
          if (c && (c.blocked_by === firstUp.key || (END.has(k) && c.state === "waiting"))) held.add(k);
        }),
      );
    const names = ORDER.filter((k) => held.has(k)).map(labelOf);
    if (names.length) blocks = `${names.join(", then ")}. ${names.length === 1 ? "It starts" : "They start"} once ${labelOf(firstUp.key)} is done.`;
  }

  // Done late, earlier on the opened PO's path: quiet, it no longer holds anything up.
  const earlier = !focusMode && scope.length > 1
    ? []
    : (ORDER.filter((k) => !END.has(k) && k !== "order_placed")
        .flatMap((k) =>
          scope
            .filter((r) => r.cells[k]?.state === "done" && r.cells[k]?.sla?.level === "breached")
            .map((r) => {
              const m = r.cells[k].sla?.late_min;
              return `${labelOf(k)}${scope.length > 1 ? ` (${r.po_number})` : ""} was done ${m != null ? `${lateText(m)} late` : "late"}, ${fmtAt(r.cells[k].actual_at)}`;
            }),
        ) as string[]);

  const handling = groupEscalations(data.escalations.filter((e) => !e.resolved_at && scopePos.has(e.po_number)));
  const others = focusMode ? rows.filter((r) => !r.highlight) : [];

  return (
    <section className={`${styles.verdictCard} ${toneCls}`} aria-label="What is happening">
      <div className={styles.verdictHead}>
        {status ? <span className={styles.statusTag}>{status.label}</span> : null}
        <h3 className={styles.verdictHeadline}>{data.headline ?? data.verdict}</h3>
      </div>
      {shown.length || handling.length || earlier.length || others.length ? (
        <div className={styles.verdictLines}>
          {shown.map((l) => (
            <div key={l.key} className={styles.verdictLine}>
              <span className={styles.verdictKey}>{l.label}</span>
              {l.body}
            </div>
          ))}
          {lines.length > shown.length ? (
            <div className={styles.verdictLine}>
              <span className={styles.verdictKey} />
              <span className={styles.muted}>…and {lines.length - shown.length} more below</span>
            </div>
          ) : null}
          {blocks ? (
            <div className={styles.verdictLine}>
              <span className={styles.verdictKey}>Holds up</span>
              <span>{blocks}</span>
            </div>
          ) : null}
          {handling.length ? (
            <div className={styles.verdictLine}>
              <span className={styles.verdictKey}>Handling</span>
              <span>
                {handling.slice(0, 2).map(({ e, pos }, i) => (
                  <span key={`${e.raised_at}-${i}`} className={styles.verdictEsc}>
                    {e.issue_type || "Escalation"} · {teamLabel(e.category)} · raised {fmtAt(e.raised_at)}
                    {e.raised_by ? ` by ${e.raised_by}` : ""}
                    {pos.size > 1 ? ` · on ${pos.size} POs` : ""}
                    {e.remarks ? <span className={styles.muted}> · “{e.remarks}”</span> : null}
                  </span>
                ))}
                {handling.length > 2 ? <span className={styles.muted}>+{handling.length - 2} more open, listed below</span> : null}
              </span>
            </div>
          ) : null}
          {earlier.length ? (
            <div className={`${styles.verdictLine} ${styles.verdictEarlier}`}>
              <span className={styles.verdictKey}>Earlier</span>
              <span>{earlier.join(" · ")}. Already done; no effect now.</span>
            </div>
          ) : null}
          {others.length ? (
            <div className={`${styles.verdictLine} ${styles.verdictEarlier}`}>
              <span className={styles.verdictKey}>Rest of order</span>
              <span>{restSummary(others)}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

// POs grouped by path: each group shows only the stages its POs use (no columns of dashes).
const PoGroups: React.FC<{ data: OcOrderPath; rows: OcOrderPathRow[]; labelOf: (k: string) => string }> = ({ data, rows: all, labelOf }) => {
  const live = all.filter((r) => r.kind !== "cancelled");
  const [onlyProblems, setOnlyProblems] = useState(live.length > 6);
  const groups = useMemo(() => {
    const byKey = new Map<string, { label: string; rows: OcOrderPathRow[] }>();
    all.forEach((r) => {
      const key = r.path_key ?? "other";
      if (!byKey.has(key)) byKey.set(key, { label: r.path_label ?? "Other", rows: [] });
      byKey.get(key)!.rows.push(r);
    });
    // Cancelled last; the group holding the PO(s) it was opened from first.
    const rank = ([k, g]: [string, { rows: OcOrderPathRow[] }]) => (k === "cancelled" ? 2 : g.rows.some((r) => r.highlight) ? 0 : 1);
    return Array.from(byKey.entries()).sort((a, b) => rank(a) - rank(b));
  }, [all]);
  // The opened PO(s) are never filtered out by "Only POs that need a look".
  const shown = (r: OcOrderPathRow) => !onlyProblems || isProblem(r) || r.highlight;
  const hidden = live.filter((r) => !shown(r)).length;
  const firstOpened = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    firstOpened.current?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [all]);
  // Scrolled to on open: the first opened PO as drawn (its group is first, it is first in its group).
  const firstOpenedId = groups.flatMap(([, g]) => g.rows).find((r) => r.highlight)?.allocation_id;
  const anyOpened = firstOpenedId != null;
  return (
    <div>
      {live.length > 1 ? (
        <div className={styles.pathToolbar}>
          <Switch size="small" checked={onlyProblems} onChange={setOnlyProblems} /> Only POs that need a look
          {hidden ? <span className={styles.muted}> · {hidden} delivered on time hidden</span> : null}
        </div>
      ) : null}
      {groups.map(([key, g]) => {
        const rows = (key === "cancelled" ? g.rows : g.rows.filter(shown))
          .slice()
          .sort((a, b) => Number(b.highlight) - Number(a.highlight));
        const cols = data.columns.filter((c) => c.key !== "order_placed" && g.rows.some((r) => r.cells[c.key]?.state !== "not_applicable"));
        // One PO: step cards; several: a grid, one row each.
        const draw = (drawRows: OcOrderPathRow[]) =>
          drawRows.length === 1 ? (
            <div ref={drawRows[0].allocation_id === firstOpenedId ? firstOpened : undefined}>
              <PoJourney row={drawRows[0]} cols={cols} labelOf={labelOf} />
            </div>
          ) : (
            <PoGrid rows={drawRows} cols={cols} firstOpenedId={firstOpenedId} firstOpened={firstOpened} />
          );
        const worst = g.rows.some((r) => Object.values(r.cells).some((c) => c.sla?.level === "breached"))
          ? styles.dotBad
          : g.rows.some((r) => Object.values(r.cells).some((c) => ["major", "minor"].includes(c.sla?.level ?? "")))
            ? styles.dotWarn
            : styles.dotGood;
        const head = (
          <>
            <span className={`${styles.dot} ${worst}`} /> {g.label} ({g.rows.length}
            {rows.length !== g.rows.length ? `, ${rows.length} shown` : ""})
          </>
        );
        if (key === "cancelled") {
          return (
            <Collapse
              key={key}
              size="small"
              ghost
              items={[{ key, label: `Cancelled (${g.rows.length})`, children: draw(g.rows) }]}
            />
          );
        }
        if (!rows.length) return null;
        // Opened from a PO / shipment (user, 2026-10-12): only the opened PO(s) stay open. Other routes
        // fold whole; on the opened PO's own route, its other POs fold under it.
        if (anyOpened) {
          const mine = rows.filter((r) => r.highlight);
          const others = rows.filter((r) => !r.highlight);
          if (!mine.length) {
            return <Collapse key={key} size="small" ghost className={styles.foldedGroup} items={[{ key, label: head, children: draw(rows) }]} />;
          }
          return (
            <div key={key} className={styles.pathGroup}>
              <div className={styles.pathGroupHead}>{head}</div>
              {draw(mine)}
              {others.length ? (
                <Collapse
                  size="small"
                  ghost
                  items={[{ key: "others", label: `Other POs on this route (${others.length})`, children: draw(others) }]}
                />
              ) : null}
            </div>
          );
        }
        return (
          <div key={key} className={styles.pathGroup}>
            <div className={styles.pathGroupHead}>{head}</div>
            {draw(rows)}
          </div>
        );
      })}
    </div>
  );
};

type Col = OcOrderPath["columns"][number];

// One PO as step cards, in path order; the picked step's details sit under them.
const PoJourney: React.FC<{ row: OcOrderPathRow; cols: Col[]; labelOf: (k: string) => string }> = ({ row, cols, labelOf }) => {
  const steps = cols.filter((c) => row.cells[c.key] && row.cells[c.key].state !== "not_applicable");
  const firstProblem = steps.find((c) => ["breached", "major", "minor"].includes(row.cells[c.key].sla?.level ?? ""))?.key;
  const initial = steps.some((c) => c.key === row.waiting_on) ? row.waiting_on : firstProblem ?? steps[steps.length - 1]?.key;
  const [picked, setPicked] = useState<string | null | undefined>(initial);
  const cancelled = row.kind === "cancelled";
  const pickedCol = steps.find((c) => c.key === picked);
  return (
    <div className={row.highlight ? `${styles.journey} ${styles.journeyOpened}` : styles.journey}>
      <PoLabel row={row} inline />
      <div className={styles.steps}>
        {steps.map((c, i) => {
          const cell = row.cells[c.key];
          const v = cellView(c.key, cell, row.waiting_on === c.key && !cancelled);
          const openEsc = cell.escalations.some((e) => !e.resolved_at);
          return (
            <button
              type="button"
              key={c.key}
              className={`${styles.step} ${TONE_CLS[v.tone]} ${picked === c.key ? styles.stepPicked : ""}`}
              aria-pressed={picked === c.key}
              onClick={() => setPicked(c.key)}
            >
              <span className={styles.stepTop}>
                <span>
                  {i + 1} · {c.owner}
                </span>
                <span className={styles.pcIcons}>
                  {cell.tech?.length ? <WarningFilled className={styles.flagOpen} /> : null}
                  {cell.escalations.length ? (
                    <span className={openEsc ? styles.flagOpen : styles.flagClosed}>
                      <FlagFilled /> {openEsc ? "Escalated" : "Resolved"}
                    </span>
                  ) : null}
                </span>
              </span>
              <span className={styles.stepName}>
                {c.label} <span className={styles.stepPill}>{v.status}</span>
              </span>
              {v.line ? <span className={styles.stepLine}>{v.line}</span> : null}
              {v.note ? <span className={styles.stepNote}>{v.note}</span> : null}
            </button>
          );
        })}
      </div>
      {pickedCol ? (
        <div className={styles.stepDetail}>
          <div className={styles.stepDetailTitle}>{pickedCol.label}</div>
          <CellDetail cell={row.cells[pickedCol.key]} stageKey={pickedCol.key} labelOf={labelOf} />
        </div>
      ) : null}
    </div>
  );
};

// Several POs: one row each, one cell per stage, details on hover / click.
const PoGrid: React.FC<{
  rows: OcOrderPathRow[];
  cols: Col[];
  firstOpenedId?: number;
  firstOpened: React.MutableRefObject<HTMLDivElement | null>;
}> = ({ rows, cols, firstOpenedId, firstOpened }) => (
  <div className={styles.gridWrap}>
    <table className={styles.pathGrid}>
      <thead>
        <tr>
          <th className={styles.poHead}>PO</th>
          {cols.map((c) => (
            <th key={c.key}>
              {c.label}
              <span className={styles.mutedSmall}> · {c.owner}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.allocation_id} className={r.highlight ? styles.highlightRow : undefined}>
            <td className={styles.poCell}>
              <div ref={r.allocation_id === firstOpenedId ? firstOpened : undefined}>
                <PoLabel row={r} />
              </div>
            </td>
            {cols.map((c) => (
              <td key={c.key} className={styles.pcTd}>
                <PathCell stageKey={c.key} stage={c.label} cell={r.cells[c.key]} current={r.waiting_on === c.key} cancelled={r.kind === "cancelled"} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PoLabel: React.FC<{ row: OcOrderPathRow; inline?: boolean }> = ({ row, inline }) => (
  <div className={inline ? styles.poLabelInline : undefined}>
    <div>
      <span className={row.highlight ? `${styles.mono} ${styles.openedPo}` : styles.mono}>{row.po_number}</span>
      {row.kind === "cancelled" ? (
        <Tag className={styles.escTag} style={{ marginLeft: 6 }}>
          Cancelled
        </Tag>
      ) : null}
    </div>
    <div className={styles.mutedSmall}>
      {[row.vendor || (row.kind === "stock" ? "From stock" : ""), fmtWeight(row.weight_kg)].filter((x) => x && x !== "–").join(" · ")}
    </div>
    {(row.tags ?? []).length ? (
      <div className={styles.pcTags}>
        {(row.tags ?? []).map((t) => (
          <Tag key={t} color={TAG_COLOR(t)} className={styles.escTag}>
            {t}
          </Tag>
        ))}
      </div>
    ) : null}
  </div>
);

const PathCell: React.FC<{ stageKey: string; stage: string; cell?: OcOrderPathCell; current: boolean; cancelled: boolean }> = ({
  stageKey,
  stage,
  cell,
  current,
  cancelled,
}) => {
  if (!cell || cell.state === "not_applicable") {
    return <div className={`${styles.pc} ${styles.pcNa}`}>{cell?.detail === "PO cancelled" ? "cancelled" : "—"}</div>;
  }
  const v = cellView(stageKey, cell, current && !cancelled);
  const openEsc = cell.escalations.some((e) => !e.resolved_at);
  const content = (
    <div className={`${styles.pc} ${TONE_CLS[v.tone]} ${current && !cancelled ? styles.pcCurrent : ""}`}>
      <span className={styles.pcMain}>{v.tone === "done" ? v.line : v.line || v.status}</span>
      {v.tone === "late" || (v.tone === "done" && v.note) ? <span className={styles.pcLate}>{v.note}</span> : null}
      <span className={styles.pcIcons}>
        {cell.tech?.length ? <WarningFilled className={styles.flagOpen} /> : null}
        {cell.escalations.length ? <FlagFilled className={openEsc ? styles.flagOpen : styles.flagClosed} /> : null}
      </span>
    </div>
  );
  return (
    <Popover trigger={["hover", "click"]} placement="bottom" title={stage} content={<CellDetail cell={cell} stageKey={stageKey} />} mouseEnterDelay={0.15}>
      {content}
    </Popover>
  );
};

// A step's details as labelled rows (user, 2026-10-12): each fact once — the SLA note's "On time" and
// "Expected …" repeat the status and the breach line, so they are dropped; the engine's own tracking
// is shown only when it differs from OMS's live status.
const SHIP_STATE = /^(Not booked|Booked|In transit|Delivered)/;

const CellDetail: React.FC<{ cell: OcOrderPathCell; stageKey?: string; labelOf?: (k: string) => string }> = ({ cell, stageKey, labelOf }) => {
  const lvl = cell.sla?.level;
  const label = (k: string) => (labelOf ? labelOf(k) : human(k));
  const late = lvl === "breached" && cell.sla?.late_min != null ? `${lateText(cell.sla.late_min)} late${cell.sla.severity === "slight" ? " (slightly)" : ""}` : "";
  const parts = (cell.sla?.note ?? "").split(/ \| | · /).map((s) => s.trim()).filter(Boolean);
  const tracking = parts.find((p) => SHIP_STATE.test(p));
  const why = parts.filter(
    (p) => p !== tracking && p !== "On time" && !/^Expected /.test(p) && !/^Delayed by /.test(p) && !/^((very|slightly) )?late$/i.test(p),
  );
  const status = cell.blocked_by
    ? `Not started · ${label(cell.blocked_by)} is stuck`
    : cell.state === "done"
      ? `Done ${fmtAt(cell.actual_at)}${late ? ` · ${late}` : lvl === "on_track" ? " · on time" : lvl ? ` · ${LEVEL[lvl].toLowerCase()}` : ""}`
      : lvl
        ? `${LEVEL[lvl]}${late ? ` · ${late}` : ""} · not done yet`
        : "Not done yet";
  const isEnd = !!stageKey && END.has(stageKey);
  // "Created: Own fleet OMS-…" → "Own fleet OMS-… (created)"
  const detail = cell.detail
    ? isEnd
      ? cell.detail
          .split(" | ")
          .map((d) => d.replace(/^([A-Z][\w ]*?): (.+)$/, (_m, st: string, rest: string) => `${rest} (${st.toLowerCase()})`))
          .join("; ")
      : cell.detail
    : "";
  return (
    <div className={styles.pcDetail}>
      <div className={styles.detailGrid}>
        <span className={styles.detailKey}>Status</span>
        <span>{status}</span>
        {cell.blocked_by ? (
          <>
            <span className={styles.detailKey}>Why blank</span>
            <span>The delay is counted on {label(cell.blocked_by)} and on Shipment, not here.</span>
          </>
        ) : null}
        {!cell.blocked_by && cell.planned_at ? (
          <>
            <span className={styles.detailKey}>Breach line</span>
            <span>{fmtAt(cell.planned_at)}</span>
          </>
        ) : null}
        {cell.state === "waiting" && cell.live ? (
          <>
            <span className={styles.detailKey}>Where now</span>
            <span>{cell.live}</span>
          </>
        ) : null}
        {tracking && tracking !== cell.live ? (
          <>
            <span className={styles.detailKey}>SLA tracking</span>
            <span>{tracking}</span>
          </>
        ) : null}
        {why.length ? (
          <>
            <span className={styles.detailKey}>Why</span>
            <span>{why.join(" · ")}</span>
          </>
        ) : null}
        {cell.sla?.excused_by ? (
          <>
            <span className={styles.detailKey}>Excused by</span>
            <span>{cell.sla.excused_by}</span>
          </>
        ) : null}
        {detail ? (
          <>
            <span className={styles.detailKey}>{isEnd ? "Truck" : "Detail"}</span>
            <span>{detail}</span>
          </>
        ) : null}
        {cell.shared_with?.length ? (
          <>
            <span className={styles.detailKey}>Same truck as</span>
            <span>{cell.shared_with.join(", ")}</span>
          </>
        ) : null}
      </div>
      {cell.tech?.map((t) => (
        <div key={t.label} className={styles.pcDetailBlock}>
          <WarningFilled className={styles.flagOpen} /> <span className={styles.strong}>{t.label}</span>
          {t.since ? <span className={styles.muted}> · since {fmtAt(t.since)}</span> : null}
          <div className={styles.mutedSmall}>{t.message}</div>
        </div>
      ))}
      {cell.escalations.map((e: OcPathEscalation, i) => (
        <div key={`${e.raised_at}-${i}`} className={styles.pcDetailBlock}>
          <FlagFilled className={e.resolved_at ? styles.flagClosed : styles.flagOpen} />{" "}
          <span className={styles.strong}>{e.issue_type || "Escalation"}</span> · {teamLabel(e.category)}
          <div className={styles.mutedSmall}>
            Raised {fmtAt(e.raised_at)}
            {e.raised_by ? ` by ${e.raised_by}` : ""} · {e.resolved_at ? `resolved ${fmtAt(e.resolved_at)}` : "open"}
          </div>
          {e.remarks ? <div className={styles.mutedSmall}>“{e.remarks}”</div> : null}
        </div>
      ))}
    </div>
  );
};

const escColumns: ColumnsType<OcOrderPath["escalations"][number]> = [
  { title: "Raised", dataIndex: "raised_at", width: 120, render: (v: string | null) => fmtAt(v) },
  { title: "PO", dataIndex: "po_number", width: 170, render: (v: string) => <span className={styles.mono}>{v}</span> },
  { title: "Stage", dataIndex: "stage_label", width: 110 },
  { title: "Issue", dataIndex: "issue_type", width: 180 },
  { title: "Team", dataIndex: "category", width: 110, render: (v: string) => teamLabel(v) },
  { title: "Remarks", dataIndex: "remarks" }, // in full: the reason is the point (user, 2026-10-12)
  { title: "Raised by", dataIndex: "raised_by", width: 130 },
  {
    title: "State",
    key: "state",
    width: 150,
    render: (_, r) => (r.resolved_at ? <span className={styles.muted}>Resolved {fmtAt(r.resolved_at)}</span> : <Tag color="red">Open</Tag>),
  },
];

const EscalationTable: React.FC<{ rows: OcOrderPath["escalations"]; openedPos?: Set<string> }> = ({ rows, openedPos }) => {
  const mine = (r: OcOrderPath["escalations"][number]) => !!openedPos?.has(r.po_number);
  // Escalations on the opened PO(s) first, marked like their PO rows above; the rest keep their order.
  const sorted = rows.slice().sort((a, b) => Number(mine(b)) - Number(mine(a)));
  const columns = escColumns.map((c) =>
    c.title === "PO"
      ? {
          ...c,
          render: (v: string) => (
            <span className={openedPos?.has(v) ? `${styles.mono} ${styles.openedPo}` : styles.mono}>{v}</span>
          ),
        }
      : c,
  );
  return rows.length ? (
    <Table
      size="small"
      rowKey={(r) => `${r.po_number}-${r.raised_at}-${r.issue_type}`}
      columns={columns}
      dataSource={sorted}
      pagination={false}
      rowClassName={(r) => (mine(r) ? styles.highlightRow : "")}
      className={styles.denseTable}
      scroll={{ x: 1000 }}
    />
  ) : (
    <div className={styles.empty}>No escalations on this order.</div>
  );
};

export default OrderPathDrawer;
